import { Flex, Spin } from 'antd';
import { Navigate, Outlet } from 'react-router-dom';
import { useListMembershipsQuery } from '@/entities/user';
import { useAppSelector } from '@/app/store/hooks';
import { ROUTES } from '@/shared/config/routes';

export interface RequireMembershipProps {
  /** Restricts to these StoreRole values; omit to allow any approved role. */
  roles?: readonly string[];
  /** `'join'` sends a missing/unapproved membership through the customer join/pending flow
   *  (`/app/*`); `'no-access'` (default) is for the staff/courier zones, which have no self-serve
   *  join flow of their own. */
  fallback?: 'join' | 'no-access';
}

/** Gate for `/app`, `/admin`, `/courier` (docs/PLAN.md §6) — checks the current store's membership
 *  status/role, not just that the user is logged in (see `RequireAuth` for that). */
export function RequireMembership({ roles, fallback = 'no-access' }: RequireMembershipProps) {
  const currentStoreId = useAppSelector((state) => state.store.currentStoreId);
  const { data: memberships, isLoading } = useListMembershipsQuery();

  if (isLoading || !memberships) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: '100vh' }}>
        <Spin size="large" />
      </Flex>
    );
  }

  const membership = currentStoreId
    ? memberships.find((m) => m.storeId === currentStoreId)
    : undefined;

  if (!membership) {
    return <Navigate to={fallback === 'join' ? ROUTES.app.join : ROUTES.noAccess} replace />;
  }
  if (membership.status !== 'APPROVED') {
    return <Navigate to={fallback === 'join' ? ROUTES.app.pending : ROUTES.noAccess} replace />;
  }
  if (roles && !roles.includes(membership.role)) {
    return <Navigate to={ROUTES.noAccess} replace />;
  }

  return <Outlet />;
}
